import { useEffect } from 'react';
import { Accordion, Link, Notification } from 'is24-corecss';
import { FormProvider, useForm } from 'react-hook-form';
import { subYears } from 'date-fns';
import type { AdacApplicationForm, AdacApplicationStatus } from '@/api/adac/adacApplication';
import { useSubmitAdacApplication } from '@/apiHooks/adacApplication.hook';
import { DatePicker } from '@/components/common/display/datePicker/DatePicker';
import { FormInput } from '@/components/common/display/formInput/FormInput';
import { Msg, useIntl } from '@/i18n';
import { APP_CONFIG } from '@/config';
import { CityAutocomplete } from './CityAutocomplete';
import { StreetAutocomplete } from './StreetAutocomplete';

const postalCodePattern = /^[0-9]{5}$/;
const submissionErrorMessages: Record<string, string> = {
  'CB-BIZ-ADAC-0': 'benefits.adac.form.applicationAlreadyExists',
  'CB-BIZ-ADAC-1': 'benefits.adac.form.unsupportedCountry',
  'CB-BIZ-ADAC-2': 'benefits.adac.form.invalidAddress',
  'CB-BIZ-ADAC-3': 'benefits.adac.form.addressValidationUnavailable',
  'CB-BIZ-ADAC-4': 'benefits.adac.form.accountDataUnavailable',
  'CB-BIZ-ADAC-5': 'benefits.adac.form.storageUnavailable',
  'CB-BIZ-INPUT_VALIDATION-0': 'benefits.adac.form.invalidInput',
};

export const ActivationForm = ({
  onSubmitted,
  onSubmittingChange,
}: {
  onSubmitted: (application: AdacApplicationStatus) => void;
  onSubmittingChange: (isSubmitting: boolean) => void;
}) => {
  const { t } = useIntl();
  const form = useForm<AdacApplicationForm>({
    progressive: true,
    defaultValues: {
      firstName: '',
      lastName: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      birthDate: '',
      countryCode: 'DEU',
    },
  });
  const {
    handleSubmit,
    setError,
    formState: { errors },
  } = form;
  const showSubmissionError = (code?: string) => {
    const messageId =
      code && Object.prototype.hasOwnProperty.call(submissionErrorMessages, code)
        ? submissionErrorMessages[code]
        : undefined;
    setError('root.submission', { message: t({ id: messageId ?? 'benefits.adac.form.submissionFailed' }) });
  };
  const { submitApplication, isSubmitting } = useSubmitAdacApplication(onSubmitted, showSubmissionError);
  const errorMessage = errors.root?.submission?.message;
  useEffect(() => {
    onSubmittingChange(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);
  const maxBirthDate = subYears(new Date(), 18);
  const requiredMessage = t({ id: 'common.errorMessages.inputRequired' });
  const textRules = {
    required: requiredMessage,
    setValueAs: (value: string) => value.trim(),
    maxLength: 255,
  };

  const submitValidatedApplication = (application: AdacApplicationForm) => submitApplication(application);
  const submitActivationApplication = handleSubmit(submitValidatedApplication);

  return (
    <FormProvider {...form}>
      {errorMessage && (
        <Notification
          role='alert'
          type='error'
          variant='flat'
          size='small'
          className='margin-bottom-l'
          title={errorMessage}
        />
      )}
      <p className='margin-bottom-l'>{t({ id: 'benefits.adac.form.description' })}</p>
      <form id='adac-application' data-dd-privacy='mask' onSubmit={submitActivationApplication} noValidate>
        <div className='grid grid-flex gutter-l'>
          <FormInput
            name='firstName'
            label={t({ id: 'benefits.adac.form.firstName' })}
            className='grid-item one-whole'
            autoComplete='given-name'
            rules={textRules}
          />
          <FormInput
            name='lastName'
            label={t({ id: 'benefits.adac.form.lastName' })}
            className='grid-item one-whole'
            autoComplete='family-name'
            rules={textRules}
          />
          <FormInput
            name='postalCode'
            label={t({ id: 'benefits.adac.form.postalCode' })}
            className='grid-item one-fourth'
            autoComplete='postal-code'
            rules={{
              ...textRules,
              maxLength: 32,
              pattern: { value: postalCodePattern, message: t({ id: 'benefits.adac.form.invalidPostalCode' }) },
            }}
          />
          <CityAutocomplete className='grid-item three-fourths' />
          <StreetAutocomplete className='grid-item three-fourths' />
          <FormInput
            name='houseNumber'
            label={t({ id: 'benefits.adac.form.houseNumber' })}
            className='grid-item one-fourth'
            autoComplete='off'
            rules={{ ...textRules, maxLength: 32 }}
          />
          <div className='grid-item one-whole'>
            <DatePicker
              id='adac-birthDate'
              name='birthDate'
              label={t({ id: 'benefits.adac.form.birthDate' })}
              required
              maxDate={maxBirthDate}
              maxDateErrorMessage={t({ id: 'benefits.adac.form.minimumAge' })}
            />
          </div>
        </div>
      </form>
      <PrivacyNotice />
    </FormProvider>
  );
};

const PrivacyNotice = () => {
  const { t } = useIntl();

  return (
    <Accordion.Root className='margin-top-l'>
      <Accordion.Item>
        <Accordion.Header>{t({ id: 'benefits.adac.form.privacyTitle' })}</Accordion.Header>
        <Accordion.Content>
          <Msg
            id='benefits.adac.form.privacyDescription'
            defaultMessage=''
            values={{
              privacyLink: (children) => (
                <Link
                  href={`${APP_CONFIG.baseApiUrl}/konto/resources/adac/ADAC-Datenschutzinformationen.pdf`}
                  target='_blank'
                  rel='noopener noreferrer'
                  variant='default'
                >
                  {children}
                </Link>
              ),
            }}
          />
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};
